// src/hooks/useNotifications.js
// ─────────────────────────────────────────────────────────────────────────────
// Shared notification system for NaijaAgro Predict
//
// FIX 1: Direct calls to api.anthropic.com are blocked by CORS when made from
//         the browser. This version routes through either:
//           a) /api/blog-generate  — a Vercel serverless function (preferred)
//           b) A public CORS proxy as fallback
//         See /api/blog-generate.js (create this file in your project).
//
// FIX 2: Generation is attempted on every mount; the 48h cooldown is checked
//         inside the function (not outside), so React StrictMode double-invoke
//         is safe.
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect, useCallback, useRef } from 'react';

const STORAGE_KEY  = 'naijaagro_notifications';
const SEEN_KEY     = 'naijaagro_seen_ids';
const LAST_GEN_KEY = 'naijaagro_last_ai_gen';
const ARTICLES_KEY = 'naijaagro_ai_articles';

const MIN_HOURS_BETWEEN_AI_BLOGS = 48;
const MAX_NOTIFICATIONS = 50;

function loadJSON(key, fallback) {
  try { return JSON.parse(localStorage.getItem(key)) ?? fallback; }
  catch { return fallback; }
}
function saveJSON(key, val) {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch {}
}

export function formatRelative(ts) {
  const diff = Date.now() - ts;
  const m = Math.floor(diff / 60000);
  const h = Math.floor(diff / 3600000);
  const d = Math.floor(diff / 86400000);
  if (m < 1)   return 'Just now';
  if (m < 60)  return `${m}m ago`;
  if (h < 24)  return `${h}h ago`;
  if (d === 1) return 'Yesterday';
  if (d < 7)   return `${d} days ago`;
  return new Date(ts).toLocaleDateString('en-NG', { day: 'numeric', month: 'short' });
}

const BLOG_TOPIC_QUEUE = [
  'Why Nigerian food prices spike before Ramadan and how households can prepare',
  'The hidden cost of transporting food from Kano to Lagos — and who pays',
  'Palm oil price history in Nigeria: from colonial export to household staple',
  'How the naira exchange rate drives the price of your daily rice in 2026',
  'Cassava farming in Nigeria: why the world\'s largest producer still imports starch',
  'The economics of suya: ingredient costs, price per stick, and margins in 2026',
  'Food budgeting for Nigerian families: how to eat well on ₦50,000 monthly',
  'Why dry season always hits Northern Nigeria\'s vegetable market hardest',
  'From farm to market: the 7-step price markup chain on Nigerian tomatoes',
  'Yam barn economics — how Benue farmers store and release yam to maximise returns',
  'Nigeria\'s fish import dependence: why titus mackerel controls protein costs',
  'Onion price cycles in Sokoto and what Dawanau market tells us nationally',
  'How insecurity in the Middle Belt changed Nigeria\'s food supply forever',
  'The story of garri: from survival food to household staple to inflation indicator',
  'Why Nigerian poultry farmers are struggling — and what it means for egg prices',
];

// ── API caller — tries Vercel proxy first, then direct (for local dev) ────────
async function callClaudeAPI(prompt) {
  const body = JSON.stringify({
    model: 'claude-sonnet-4-20250514',
    max_tokens: 1200,
    messages: [{ role: 'user', content: prompt }],
  });

  // 1️⃣ Try your own Vercel serverless proxy (no CORS issues)
  try {
    const res = await fetch('/api/blog-generate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      signal: AbortSignal.timeout(30000),
    });
    if (res.ok) {
      const data = await res.json();
      // Proxy can return the full Anthropic response or just { text }
      return data?.content?.[0]?.text ?? data?.text ?? null;
    }
  } catch (_) {
    // Proxy not available — likely local dev
  }

  // 2️⃣ Fallback: direct call (works in local dev, blocked in production by CORS)
  try {
    const apiKey = process.env.REACT_APP_ANTHROPIC_API_KEY;
    if (!apiKey) throw new Error('No API key');
    const res = await fetch('https://api.anthropic.com/v1/messages', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': apiKey,
        'anthropic-version': '2023-06-01',
        'anthropic-dangerous-direct-browser-access': 'true', // required for browser direct calls
      },
      body,
      signal: AbortSignal.timeout(30000),
    });
    if (!res.ok) throw new Error(`API ${res.status}`);
    const data = await res.json();
    return data?.content?.[0]?.text ?? null;
  } catch (err) {
    console.error('[NaijaAgro] Claude API call failed:', err.message);
    return null;
  }
}

// ── Main hook ────────────────────────────────────────────────────────────────
export function useNotifications({ onNewAIArticle } = {}) {
  const [notifications, setNotifications] = useState(() => loadJSON(STORAGE_KEY, []));
  const [seenIds, setSeenIds]             = useState(() => new Set(loadJSON(SEEN_KEY, [])));
  const [generating, setGenerating]       = useState(false);
  const generatingRef = useRef(false);

  useEffect(() => {
    saveJSON(STORAGE_KEY, notifications.slice(0, MAX_NOTIFICATIONS));
  }, [notifications]);

  useEffect(() => {
    saveJSON(SEEN_KEY, [...seenIds]);
  }, [seenIds]);

  const unseenCount     = notifications.filter(n => !seenIds.has(n.id)).length;
  const unseenBlogCount = notifications.filter(n => n.type === 'blog' && !seenIds.has(n.id)).length;

  const markAllSeen = useCallback(() => {
    setSeenIds(prev => {
      const next = new Set(prev);
      notifications.forEach(n => next.add(n.id));
      return next;
    });
  }, [notifications]);

  const markSeen = useCallback((id) => {
    setSeenIds(prev => { const next = new Set(prev); next.add(id); return next; });
  }, []);

  const addNotification = useCallback((notif) => {
    const full = {
      ...notif,
      id: notif.id || `${Date.now()}-${Math.random()}`,
      timestamp: notif.timestamp || Date.now(),
      read: false,
    };
    setNotifications(prev => [full, ...prev].slice(0, MAX_NOTIFICATIONS));
    return full;
  }, []);

  const addNewsNotification = useCallback((title, body) => {
    return addNotification({ type: 'news', title, body });
  }, [addNotification]);

  // ── Regulated AI blog generation ─────────────────────────────────────────
  const generateAIBlog = useCallback(async () => {
    // Double-invoke guard (React StrictMode)
    if (generatingRef.current) return null;

    // 48h cooldown check
    const lastGen = loadJSON(LAST_GEN_KEY, 0);
    const hoursElapsed = (Date.now() - lastGen) / 3600000;
    if (hoursElapsed < MIN_HOURS_BETWEEN_AI_BLOGS) {
      const remaining = Math.round(MIN_HOURS_BETWEEN_AI_BLOGS - hoursElapsed);
      console.info(`[NaijaAgro] AI blog cooldown: ${remaining}h remaining`);
      return null;
    }

    generatingRef.current = true;
    setGenerating(true);

    try {
      const existingArticles = loadJSON(ARTICLES_KEY, []);
      const topicIndex = existingArticles.length % BLOG_TOPIC_QUEUE.length;
      const topic = BLOG_TOPIC_QUEUE[topicIndex];

      const prompt = `You are a senior editor for NaijaAgro Predict, a Nigerian agricultural market intelligence platform. Write a compelling blog post for Nigerian readers — farmers, traders, households, and food businesses.

TOPIC: ${topic}
TONE: Mix of informative and storytelling — clear, engaging, authentically Nigerian
TARGET LENGTH: 600–800 words

RULES:
- Open with a punchy hook (striking statistic, proverb, or vivid scenario)
- Include real or realistic Naira price examples
- Include "Why does this happen?" explanation
- Include one historical context angle
- Include practical actionable tips for ordinary Nigerians
- Use Nigerian cultural context (states, markets, crops, Pidgin phrases where natural)
- Structure with markdown ## headings, - bullets, **bold** for key terms
- End with a strong closing paragraph
- DO NOT use filler phrases like "In conclusion" or "In today's world"
- Max 2 emojis

FORMAT (strictly follow this structure):
TITLE: [compelling headline]
EXCERPT: [2-sentence teaser]
CATEGORY: [one of: Market Analysis, Commodity Guide, Farmer Guide, Economic Context, Food & Diet, Agro Finance]
READ_TIME: [X min read]
CONTENT:
[full article in markdown]`;

      const raw = await callClaudeAPI(prompt);
      if (!raw) throw new Error('No response from Claude API');

      const titleMatch    = raw.match(/TITLE:\s*(.+)/);
      const excerptMatch  = raw.match(/EXCERPT:\s*(.+)/);
      const categoryMatch = raw.match(/CATEGORY:\s*(.+)/);
      const readTimeMatch = raw.match(/READ_TIME:\s*(.+)/);
      const contentMatch  = raw.match(/CONTENT:\s*\n([\s\S]+)/);

      const article = {
        id:       `ai-${Date.now()}`,
        title:    titleMatch?.[1]?.trim()    || 'New Market Insight',
        excerpt:  excerptMatch?.[1]?.trim()  || '',
        category: categoryMatch?.[1]?.trim() || 'Market Analysis',
        readTime: readTimeMatch?.[1]?.trim() || '5 min read',
        content:  contentMatch?.[1]?.trim()  || raw,
        date:     new Date().toLocaleDateString('en-NG', { day: 'numeric', month: 'long', year: 'numeric' }),
        timestamp: Date.now(),
        isAI:     true,
        image:    'https://images.unsplash.com/photo-1464226184884-fa280b87c399?w=800&q=80',
        author:   'NaijaAgro AI Research Desk',
      };

      const updated = [article, ...existingArticles].slice(0, 30);
      saveJSON(ARTICLES_KEY, updated);
      saveJSON(LAST_GEN_KEY, Date.now());

      addNotification({
        id:        `blog-${article.id}`,
        type:      'blog',
        title:     article.title,
        body:      article.excerpt,
        articleId: article.id,
        timestamp: Date.now(),
      });

      onNewAIArticle?.(article);
      console.info('[NaijaAgro] AI blog generated successfully:', article.title);
      return article;
    } catch (err) {
      console.error('[NaijaAgro] AI blog generation failed:', err.message);
      return null;
    } finally {
      generatingRef.current = false;
      setGenerating(false);
    }
  }, [addNotification, onNewAIArticle]);

  const getAIArticles = useCallback(() => loadJSON(ARTICLES_KEY, []), []);

  return {
    notifications,
    unseenCount,
    unseenBlogCount,
    seenIds,
    markAllSeen,
    markSeen,
    addNotification,
    addNewsNotification,
    generateAIBlog,
    getAIArticles,
    generating,
    formatRelative,
  };
}